import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllBookings } from '../../api';
import PaymentStatus from '../../components/PaymentStatus';
import styles from './BookingHistory.module.css';

const BookingHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const userEmail = localStorage.getItem('userEmail');
        const data = await getAllBookings(userEmail);
        // Ensure we have an array and filter out any invalid entries
        const validBookings = Array.isArray(data) ? data.filter(booking => booking && typeof booking === 'object') : [];
        setBookings(validBookings);
        setError(null);
      } catch (err) {
        setError('Failed to load booking history');
        console.error('Error fetching bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Booked':
        return styles.statusBooked;
      case 'Pending':
        return styles.statusPending;
      case 'Completed':
        return styles.statusCompleted;
      case 'Cancelled':
        return styles.statusCancelled;
      default:
        return styles.statusDefault;
    }
  };

  const handleBack = () => {
    navigate('/user/dashboard');
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.loadingContainer}>
            <h2 className={styles.loadingText}>Loading booking history...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>Booking History</h1>
          <button
            onClick={handleBack}
            className={styles.backButton}
          >
            Back to Dashboard
          </button>
        </div>

        <div className={styles.mainContent}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {bookings.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>No booking history found</h3>
              <p>You don't have any bookings yet.</p>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead className={styles.tableHeader}>
                  <tr>
                    <th>Doctor</th>
                    <th>Date</th>
                    <th>Slot</th>
                    <th>Fees</th>
                    <th>Status</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking, index) => (
                    <tr key={booking._id || index} className={styles.tableRow}>
                      <td className={styles.tableCell}>
                        {booking.doctor ?
                          (typeof booking.doctor === 'string' ?
                            booking.doctor :
                            (booking.doctor.name || 'Unknown Doctor')
                          ) : 'N/A'}
                      </td>
                      <td className={styles.tableCell}>{formatDate(booking.date)}</td>
                      <td className={styles.tableCell}>{booking.slot}</td>
                      <td className={`${styles.tableCell} ${styles.feesCell}`}>
                        {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(booking.fees || 0))}
                      </td>
                      <td className={styles.tableCell}>
                        <span className={`${styles.statusBadge} ${getStatusBadgeClass(booking.status)}`}>
                          {booking.status}
                        </span>
                      </td>
                      <td className={styles.tableCell}>
                        <PaymentStatus
                          status={booking.paymentStatus}
                          paymentMethod={booking.paymentMethod}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingHistory; 